/**
 * Resolve chapter-list StoryCraft border score from AI and/or editor assessments.
 */

export type ScoreBlendMode = 'storycraft' | 'editor' | 'weighted';

export type ScoreBlendSettings = {
  mode: ScoreBlendMode;
  /** 0 = editor only, 1 = StoryCraft only (when mode is weighted). */
  weightStoryCraft: number;
};

export const DEFAULT_SCORE_BLEND: ScoreBlendSettings = {
  mode: 'storycraft',
  weightStoryCraft: 0.5,
};

export function normalizeScoreBlend(raw: unknown): ScoreBlendSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SCORE_BLEND };
  const o = raw as Record<string, unknown>;
  const mode =
    o.mode === 'editor' || o.mode === 'weighted' || o.mode === 'storycraft'
      ? o.mode
      : DEFAULT_SCORE_BLEND.mode;
  let weight =
    typeof o.weightStoryCraft === 'number' && Number.isFinite(o.weightStoryCraft)
      ? o.weightStoryCraft
      : DEFAULT_SCORE_BLEND.weightStoryCraft;
  weight = Math.min(1, Math.max(0, weight));
  return { mode, weightStoryCraft: weight };
}

function isScoreBlock(v: unknown): v is { score: number } {
  if (!v || typeof v !== 'object') return false;
  const score = (v as { score?: unknown }).score;
  return typeof score === 'number' && Number.isFinite(score);
}

/** Average 1–5 from a StoryCraft / editor assessment JSON blob. */
export function averageAssessmentScore(assessment: unknown): number | null {
  if (!assessment || typeof assessment !== 'object') return null;
  const a = assessment as Record<string, unknown>;
  const keys = [
    'plotProgression',
    'characterDevelopment',
    'themeReinforcement',
    'pacing',
    'conflictTension',
    'hookEnding',
  ] as const;
  let sum = 0;
  let n = 0;
  for (const k of keys) {
    if (isScoreBlock(a[k])) {
      sum += a[k].score;
      n += 1;
    }
  }
  if (n === 0) return null;
  return sum / n;
}

/**
 * Combine optional averages (1–5). Missing side in weighted mode falls back to the present side.
 * Returns null when neither score exists.
 */
export function resolveChapterListScore(
  storyCraftAverage: number | null | undefined,
  editorAverage: number | null | undefined,
  blend: ScoreBlendSettings = DEFAULT_SCORE_BLEND
): number | null {
  const sc =
    typeof storyCraftAverage === 'number' && Number.isFinite(storyCraftAverage)
      ? storyCraftAverage
      : null;
  const ed =
    typeof editorAverage === 'number' && Number.isFinite(editorAverage) ? editorAverage : null;

  if (blend.mode === 'storycraft') return sc;
  if (blend.mode === 'editor') return ed;

  // weighted
  if (sc != null && ed != null) {
    const w = blend.weightStoryCraft;
    return sc * w + ed * (1 - w);
  }
  return sc ?? ed;
}

/** Shared 1–5 → border color (matches web/desktop chapter list). */
export function storyCraftScoreBorderColor(
  score: number | null | undefined,
  options?: { forChapter?: boolean }
): string {
  if (options?.forChapter === false) return 'hsl(var(--border))';
  if (score == null || !Number.isFinite(score)) {
    return 'hsl(var(--muted-foreground) / 0.25)';
  }
  // Saturated greens / ambers / reds so score bands read clearly on dark UI.
  if (score >= 4) return '#22c55e';
  if (score >= 3) return '#f59e0b';
  return '#ef4444';
}
